import { getAppCheckToken } from "./firebase";

interface FetchOptions extends RequestInit {
  userId?: string;
  projectId?: string;
  skipContentType?: boolean;
}

/**
 * Get the backend API base URL.
 * Uses VITE_BACKEND_URL environment variable (single source of truth).
 */
function getApiBaseUrl(): string {
  return import.meta.env.VITE_BACKEND_URL || "";
}

/**
 * Centralized fetch wrapper that automatically:
 * - Prepends VITE_BACKEND_URL to relative paths
 * - Adds X-Firebase-AppCheck header (for App Check verification)
 * - Adds X-User-ID header (when userId is provided)
 * - Adds X-Project-ID header (when projectId is provided)
 * - Adds Content-Type: application/json (unless skipContentType is true)
 *
 * Use this instead of native fetch() for all backend API calls.
 */
export async function fetch(
  url: string,
  options: FetchOptions = {}
): Promise<Response> {
  const { userId, projectId, skipContentType, ...fetchOptions } = options;

  // Prepend base URL to relative paths
  const fullUrl = url.startsWith("/") ? `${getApiBaseUrl()}${url}` : url;

  const headers: Record<string, string> = {
    ...(skipContentType ? {} : { "Content-Type": "application/json" }),
    ...((fetchOptions.headers as Record<string, string>) || {}),
  };

  if (userId) {
    headers["X-User-ID"] = userId;
  }

  if (projectId) {
    headers["X-Project-ID"] = projectId;
  }

  const appCheckToken = await getAppCheckToken();
  if (appCheckToken) {
    headers["X-Firebase-AppCheck"] = appCheckToken;
  }

  return globalThis.fetch(fullUrl, { ...fetchOptions, headers });
}
